import { Box, Button, CircularProgress } from '@mui/material';
import React from 'react';

interface WizardNavigationProps {
  /** Label for the primary (blue) button */
  primaryLabel: string;
  /** Callback when primary button is clicked */
  onPrimary: () => void;
  /** Whether the primary button is disabled */
  primaryDisabled?: boolean;
  /** Whether the primary button should show a loading spinner */
  primaryLoading?: boolean;
  /** Label for the secondary (outlined) button - if not provided, secondary button is hidden */
  secondaryLabel?: string;
  /** Callback when secondary button is clicked */
  onSecondary?: () => void;
  /** Whether the secondary button is disabled */
  secondaryDisabled?: boolean;
}

/**
 * WizardNavigation - Bottom button area for installation guide steps
 * 
 * Provides consistent button styling matching the existing app patterns:
 * - Primary button: Full-width, blue background, white text
 * - Secondary button: Full-width, outlined, blue border/text
 */
export const WizardNavigation: React.FC<WizardNavigationProps> = ({
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  primaryLoading = false,
  secondaryLabel,
  onSecondary,
  secondaryDisabled = false,
}) => {
  return (
    <Box
      sx={{
        mt: 'auto',
        pt: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {/* Primary button */}
      <Button
        fullWidth
        variant="contained"
        onClick={onPrimary}
        disabled={primaryDisabled || primaryLoading}
        aria-label={primaryLabel}
        sx={{
          py: { xs: 1.5, sm: 1.25 },
          borderRadius: 1,
          minHeight: { xs: '48px', sm: '40px' },
          '&:disabled': {
            bgcolor: 'action.disabledBackground',
            color: 'action.disabled',
          },
        }}
      >
        {primaryLoading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          primaryLabel
        )}
      </Button>

      {/* Secondary button (optional) */}
      {secondaryLabel && onSecondary && (
        <Button
          fullWidth
          variant="outlined"
          onClick={onSecondary}
          disabled={secondaryDisabled}
          sx={{
            py: { xs: 1.5, sm: 1.25 },
            borderRadius: 1,
            minHeight: { xs: '48px', sm: '40px' },
          }}
        >
          {secondaryLabel}
        </Button>
      )}
    </Box>
  );
};
